import { useRef } from 'react';
import { CalendarCheck } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface StartDatePickerProps {
  startDate?: string | null;
  onChange?: (startDate: string | null) => void;
  stopDragPointerDown?: boolean;
}

export function StartDatePicker({
  startDate,
  onChange,
  stopDragPointerDown = true,
}: StartDatePickerProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const editable = !!onChange;

  const formatted = startDate
    ? new Date(startDate).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
      })
    : null;

  const textColor = startDate ? 'text-grey-500' : 'text-grey-300';

  if (!editable) {
    if (!formatted) return null;
    return (
      <span className={cn('flex items-center gap-1 flex-shrink-0', textColor)}>
        <CalendarCheck className="w-3 h-3" />
        <span>{formatted}</span>
      </span>
    );
  }

  return (
    <div className="relative flex-shrink-0">
      <button
        type="button"
        onPointerDown={stopDragPointerDown ? (e) => e.stopPropagation() : undefined}
        onClick={(e) => {
          e.stopPropagation();
          inputRef.current?.showPicker?.();
        }}
        className={cn(
          'flex items-center gap-1 rounded px-1.5 py-0.5 transition-colors hover:bg-grey-100',
          textColor,
        )}
        title={startDate ? 'Change start date' : 'Set start date'}
      >
        <CalendarCheck className="w-3 h-3" />
        {formatted && <span>{formatted}</span>}
      </button>
      <input
        ref={inputRef}
        type="date"
        className="absolute inset-0 opacity-0 pointer-events-none"
        value={startDate ? new Date(startDate).toISOString().split('T')[0] : ''}
        onChange={(e) => {
          e.stopPropagation();
          onChange(e.target.value || null);
        }}
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        tabIndex={-1}
      />
    </div>
  );
}
